import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BellRing, TrendingDown, Trash2, Loader2, PackageCheck, History } from 'lucide-react';
import { accountAlertsAPI } from '../services/api';
import { alertsBadge } from '../services/alertsBadge';
import { useT } from '../services/i18n';
import { prodName } from '../services/catalogI18n';
import { toast } from 'sonner';
import MyVehicleAlerts from './MyVehicleAlerts';
import MyNewArrivalsSubs from './MyNewArrivalsSubs';

const KindBadge = ({ kind, t }) => (
  kind === 'price'
    ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-800 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full" data-testid="alert-kind-price"><TrendingDown size={11} /> {t('alerts.kindPrice')}</span>
    : <span className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-800 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full" data-testid="alert-kind-stock"><BellRing size={11} /> {t('alerts.kindStock')}</span>
);

// Mon compte › Mes alertes : active back-in-stock / price-drop alerts with one-click removal + recent history
const MyAlertsSection = () => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => accountAlertsAPI.list().then(setData).catch(() => setData({ items: [], history: [] })), []);
  useEffect(() => { load(); alertsBadge.markSeen(); }, [load]);

  const remove = async (id) => {
    setBusy(id);
    try { await accountAlertsAPI.remove(id); setData(d => ({ ...d, items: d.items.filter(i => i.id !== id) })); toast.success(t('alerts.removed')); } catch { toast.error(t('alerts.error')); } finally { setBusy(''); }
  };
  const removeAll = async () => {
    setBusy('all');
    try { await accountAlertsAPI.removeAll(); setData(d => ({ ...d, items: [] })); toast.success(t('alerts.removedAll')); } catch { toast.error(t('alerts.error')); } finally { setBusy(''); }
  };
  const fmt = (iso) => new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { day: '2-digit', month: 'short', year: 'numeric' });

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-orange-500" /></div>;
  return (
    <div data-testid="my-alerts">
      <p className="text-sm text-gray-600 mb-4">{t('alerts.intro')}</p>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900" data-testid="my-alerts-count">{t('alerts.active', { n: data.items.length, count: data.items.length })}</h3>
        {data.items.length > 1 && <button type="button" onClick={removeAll} disabled={busy === 'all'} data-testid="my-alerts-remove-all" className="text-xs text-gray-500 hover:text-red-600 underline">{t('alerts.removeAll')}</button>}
      </div>
      {!data.items.length ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-8 text-center" data-testid="my-alerts-empty">
          <BellRing size={28} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">{t('alerts.empty')}</p>
          <Link to="/pieces" className="inline-block mt-3 text-sm font-semibold text-orange-600 hover:underline">{t('cart.discover')}</Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {data.items.map(a => (
            <li key={a.id} data-testid={`my-alert-${a.id}`} className="bg-white rounded-xl border border-gray-200 p-3 flex gap-3 items-center">
              <Link to={`/produit/${a.product.slug || a.product.id}`} className="w-16 h-16 shrink-0 rounded bg-gray-50 flex items-center justify-center overflow-hidden"><img src={a.product.image} alt="" className="w-full h-full object-contain" /></Link>
              <div className="flex-1 min-w-0">
                <KindBadge kind={a.kind} t={t} />
                <Link to={`/produit/${a.product.slug || a.product.id}`} className="block font-medium text-gray-900 text-sm truncate hover:text-orange-600 mt-1" data-testid={`my-alert-name-${a.id}`}>{prodName(a.product, lang)}</Link>
                <p className="text-xs text-gray-500">{a.product.brand} · {t('common.ref')} {a.product.ref} · {t('alerts.since', { d: fmt(a.created_at) })}</p>
                <p className="text-xs mt-0.5">
                  {a.kind === 'price'
                    ? <span className="text-gray-600">{t('alerts.priceWatch', { p: Number(a.price_at || 0).toFixed(2) })} · <strong>{Number(a.product.price || 0).toFixed(2)} €</strong> {t('alerts.now')}</span>
                    : a.product.in_stock ? <span className="text-green-700 font-medium inline-flex items-center gap-1"><PackageCheck size={12} /> {t('alerts.backSoon')}</span> : <span className="text-red-600">{t('stock.out')}</span>}
                </p>
              </div>
              <button type="button" onClick={() => remove(a.id)} disabled={busy === a.id} data-testid={`my-alert-remove-${a.id}`} title={t('alerts.remove')} className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 border border-gray-200 hover:border-red-300 rounded-md px-2.5 py-1.5 disabled:opacity-50">
                {busy === a.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} <span className="hidden sm:inline">{t('alerts.remove')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {data.history.length > 0 && (
        <div className="mt-8" data-testid="my-alerts-history">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-2"><History size={16} className="text-gray-400" /> {t('alerts.history')}</h3>
          <ul className="divide-y divide-gray-100 bg-white rounded-xl border border-gray-200">
            {data.history.map(h => (
              <li key={h.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`my-alert-history-${h.id}`}>
                <KindBadge kind={h.kind} t={t} />
                <Link to={`/produit/${h.product.slug || h.product.id}`} className="flex-1 truncate text-gray-800 hover:text-orange-600">{prodName(h.product, lang)} <span className="text-gray-400">· {h.product.brand}</span></Link>
                <span className="text-xs text-gray-500 whitespace-nowrap">{t('alerts.sentOn', { d: fmt(h.notified_at) })}{h.notified_price != null ? ` · ${Number(h.notified_price).toFixed(2)} €` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <MyVehicleAlerts />
      <MyNewArrivalsSubs />
    </div>
  );
};

export default MyAlertsSection;
